import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { handleDeleteDayPlanDetail } from "../Plan/hooks/PlanHooks";
import { handleSavePlanGlobal, handleUpdatePlanGlobal } from "./hooks/GlobalPlansHooks";
import { readPlanCategoriesRequest, savePlanCategoryRequest } from "../../store/Plan/planActions";
import GalleryPickerField from "../../components/ImageGallery/GalleryPickerField";
import { getBibleSearchAction } from "../../store/Bible/BibleAction";
import { AiOutlineDelete, AiOutlinePlus, AiOutlineClose } from "react-icons/ai";
import LanguageDrawer from "../../components/universal/LanguageDrawer";
import CustomTabsV2 from "../../components/universal/CustomTabsV2";
import CollapsibleRightPanel from "../../components/universal/CollapsibleRightPanel";
import UploadControlsV2 from "../../components/TWG/UploadControlsV2";
import { uploadAssestsV2Action } from "../../store/Languages/LanguageAction";
import Cropper from "react-easy-crop";

const GlobalPlanForm = ({ rowData = {}, onClose, typeofentity }) => {
  const dispatch = useDispatch();

  const [planName, setPlanName] = useState("");
  const [description, setDescription] = useState("");
  const [noofDays, setnoofDays] = useState(1);
  const [galleryObjId, setCoverImage] = useState(null);
  const [coverImageUrls, setCoverImageUrls] = useState({ webUploadUrl: "" });
  const [dayPlans, setDayPlans] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [currentDayIndex, setCurrentDayIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedVerses, setSelectedVerses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedViewMode, setSelectedViewMode] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [languagesList, setLanguagesList] = useState([]);

  // Cover image crop states (match Image Gallery flow)
  const [coverSelectedImage, setCoverSelectedImage] = useState(null);
  const [coverActiveTab, setCoverActiveTab] = useState("desktop");
  const [coverCrop, setCoverCrop] = useState({ x: 0, y: 0 });
  const [coverZoom, setCoverZoom] = useState(1);
  const [coverCroppedAreaPixels, setCoverCroppedAreaPixels] = useState(null);
  const [coverIsCropping, setCoverIsCropping] = useState(false);
  const [coverCroppedImages, setCoverCroppedImages] = useState({
    desktop: "",
    tablet: "",
    mobile: "",
  });
  const coverOriginalBlobRef = useRef(null);

  const [activeTab, setActiveTab] = useState("en");
  const [activeLanguageDetails, setActiveLanguageDetails] = useState({ language_code: "en", language_name: "English" });
  const [languagemeta, setLanguagemeta] = useState([]);
  const [isLanguageSave, setIsLanguageSave] = useState(false);
  const { userInfo } = useSelector((state) => state.user);

  const filteredOptions = categories.filter(category =>
    (category.categoryName || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    dispatch(readPlanCategoriesRequest({ page: 1, limit: 100 }, (res) => {
      if (res.type === 1) {
        setCategories(res.data || []);
      }
    }));
  }, [dispatch]);

  useEffect(() => {
    if (categories.length > 0 && rowData?.categoryObjId) {
      const cat = categories.find(c => (c._id === rowData.categoryObjId) || (c.categoryObjId === rowData.categoryObjId));
      if (cat) {
        setSelectedCategory(cat);
        setSearchTerm(cat.categoryName);
        if (cat.layoutStyle && !selectedViewMode) {
          setSelectedViewMode(Number(cat.layoutStyle));
        }
      }
    }
  }, [categories, rowData]);

  // Watch for changes in noofDays and adjust dayPlans accordingly
  useEffect(() => {
    const currentLength = dayPlans.length;

    if (noofDays > currentLength) {
      // Add empty day plans
      const newDays = Array.from({ length: noofDays - currentLength }, (_, i) => ({
        dayNumber: currentLength + i + 1,
        dayPlanName: "",
        dayPlanDescription: "",
        youtubeVideoUrl: "",
        verseObjId: [],
        dayPlanDetailId: "", // For updates
      }));
      setDayPlans([...dayPlans, ...newDays]);
    } else if (noofDays < currentLength) {
      // Remove extra day plans
      setDayPlans(dayPlans.slice(0, noofDays));
    }
  }, [noofDays]);

  useEffect(() => {
    if (rowData && Object.keys(rowData).length > 0) {
      setPlanName(rowData.planName || "");
      setDescription(rowData.description || "");
      setnoofDays(rowData.noofDays || 1);

      if (rowData.coverImage || rowData.galleryObjId || rowData.plansCoverImageUrl) {
        const img = rowData.coverImage || rowData.galleryObjId || rowData.plansCoverImageUrl;
        setCoverImage(img);
        if (typeof img === 'string' && img.startsWith('http')) {
          setCoverImageUrls({ webUploadUrl: img });
          setCoverCroppedImages({ desktop: img, tablet: img, mobile: img });
        } else if (rowData.gallery?.imageUrl) {
          setCoverImageUrls({ webUploadUrl: rowData.gallery.imageUrl });
          setCoverCroppedImages({
            desktop: rowData.gallery.imageUrl,
            tablet: rowData.gallery.imageUrl,
            mobile: rowData.gallery.imageUrl,
          });
        }
      }

      if (rowData.layoutStyle) {
        setSelectedViewMode(Number(rowData.layoutStyle));
      }

      // Handle both old and new data structures
      if (Array.isArray(rowData.dayPlans)) {
        const formattedDayPlans = rowData.dayPlans.map((day, index) => {
          const versesFromDetails = Array.isArray(day.dayplandetails)
            ? day.dayplandetails.map((v) => ({
                id: `${v.bookName}-${v.chapterId}-${v.verseStart}`,
                _id: v.dayplandetObjId,
                book_name: v.bookName,
                book_id: v.bookId,
                chapter: v.chapterId,
                verse_start: v.verseStart,
                text: v.verseText,
                reference: `${v.bookName} ${v.chapterId}:${v.verseStart}`,
                filesetid: v.versionid,
              }))
            : [];

          return {
            _id: day.dayplanObjId || day._id,
            dayNumber: Number(day.dayNumber || index + 1),
            dayPlanName: day.dayplanname || "",
            dayPlanDescription: day.dayplandescription || "",
            youtubeVideoUrl: day.dayplandetails?.[0]?.youtubevideourl || "",
            dayPlanDetailId: "", // handled per verse
            verseObjId: versesFromDetails,
          };
        });

        setDayPlans(formattedDayPlans);
      }

    } else {
      // Reset for new plan
      setDayPlans([{
        dayNumber: 1,
        dayPlanName: "",
        dayPlanDescription: "",
        youtubeVideoUrl: "",
        verseObjId: [],
        dayPlanDetailId: "",
      }]);
    }
  }, [rowData]);

  const getCoverAspect = (tab) =>
    tab === "desktop" ? 16 / 9 : tab === "tablet" ? 4 / 3 : 9 / 16;

  const closeCoverCropper = () => {
    setCoverSelectedImage(null);
    setCoverActiveTab("desktop");
    setCoverCrop({ x: 0, y: 0 });
    setCoverZoom(1);
    setCoverCroppedAreaPixels(null);
    setCoverIsCropping(false);
  };

  const onCoverCropComplete = useCallback((_, croppedPixels) => {
    setCoverCroppedAreaPixels(croppedPixels);
  }, []);

  const loadCoverImageAsBlob = async (imageSrc) => {
    const response = await fetch(imageSrc, {
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "image/*" },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return { blob, blobUrl };
  };

  const getCoverCroppedImg = async (imageSrc, pixelCrop) => {
    try {
      const response = await fetch(imageSrc, {
        mode: "cors",
        credentials: "omit",
        headers: { Accept: "image/*" },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      const imageBitmap = await createImageBitmap(blob);

      const offscreenCanvas = new OffscreenCanvas(
        Math.max(1, Math.floor(pixelCrop.width)),
        Math.max(1, Math.floor(pixelCrop.height))
      );
      const ctx = offscreenCanvas.getContext("2d");

      if (!ctx) {
        throw new Error("Failed to get 2d context");
      }

      ctx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
      ctx.drawImage(
        imageBitmap,
        Math.floor(pixelCrop.x),
        Math.floor(pixelCrop.y),
        Math.floor(pixelCrop.width),
        Math.floor(pixelCrop.height),
        0,
        0,
        offscreenCanvas.width,
        offscreenCanvas.height
      );

      const croppedBlob = await offscreenCanvas.convertToBlob({
        type: "image/jpeg",
        quality: 0.9,
      });

      imageBitmap.close();

      const url = URL.createObjectURL(croppedBlob);
      return { blob: croppedBlob, url };
    } catch (error) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.floor(pixelCrop.width));
            canvas.height = Math.max(1, Math.floor(pixelCrop.height));
            const ctx = canvas.getContext("2d");

            ctx.drawImage(
              img,
              Math.floor(pixelCrop.x),
              Math.floor(pixelCrop.y),
              Math.floor(pixelCrop.width),
              Math.floor(pixelCrop.height),
              0,
              0,
              canvas.width,
              canvas.height
            );

            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error("Canvas is empty"));
                  return;
                }
                const url = URL.createObjectURL(blob);
                resolve({ blob, url });
              },
              "image/jpeg",
              0.9
            );
          } catch (drawError) {
            reject(new Error("Canvas fallback failed: " + drawError.message));
          }
        };
        img.onerror = () => reject(new Error("Failed to load image in fallback"));
        const separator = imageSrc.includes("?") ? "&" : "?";
        img.src = imageSrc + separator + "t=" + Date.now();
      });
    }
  };

  const handleCoverCropSave = async () => {
    if (!coverSelectedImage || !coverSelectedImage.url) {
      toast.error("No image selected for cropping.");
      return;
    }
    if (!coverCroppedAreaPixels) {
      toast.error("Please make a crop selection before saving.");
      return;
    }
    if (coverIsCropping) return;

    setCoverIsCropping(true);

    try {
      const { blob } = await getCoverCroppedImg(
        coverSelectedImage.url,
        coverCroppedAreaPixels
      );

      const filename = `plan-cover-${coverActiveTab}-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });
      const formData = new FormData();
      formData.append("file", file);

      dispatch(
        uploadAssestsV2Action({
          apiPayloadRequest: formData,
          uploadProgressId: `plan_cover_${coverActiveTab}_crop`,
          callback: (res) => {
            setCoverIsCropping(false);
            if (res?.url) {
              setCoverCroppedImages((prev) => {
                const updated = {
                  ...prev,
                  [coverActiveTab]: res.url,
                };
                if (coverActiveTab === "desktop") {
                  setCoverImageUrls((prevUrls) => ({
                    ...prevUrls,
                    webUploadUrl: res.url,
                  }));
                  setCoverImage(res.url);
                }
                return updated;
              });

              if (coverActiveTab === "mobile") {
                closeCoverCropper();
              } else {
                setCoverActiveTab(
                  coverActiveTab === "desktop" ? "tablet" : "mobile"
                );
                setCoverCrop({ x: 0, y: 0 });
                setCoverZoom(1);
                setCoverCroppedAreaPixels(null);
              }
            }
          },
        })
      );
    } catch (err) {
      setCoverIsCropping(false);
      toast.error("Failed to crop image. Please try again.");
    }
  };

  const handleSearch = useCallback((query) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!query?.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    const timeout = setTimeout(() => {
      dispatch(
        getBibleSearchAction({
          apiPayloadRequest: {
            question: query,
          },
          callback: (res) => {
            setSearchResults(res?.data ?? []);
            setIsSearching(false);
          },
        })
      );
    }, 500);

    setSearchTimeout(timeout);
  }, [dispatch, searchTimeout]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  const openSearchModal = (dayIndex) => {
    setCurrentDayIndex(dayIndex);
    setSearchQuery("");
    setSearchResults([]);
    const existingVerses = dayPlans[dayIndex]?.verseObjId || [];
    setSelectedVerses(existingVerses);
    setShowSearchModal(true);
  };

  const closeSearchModal = () => {
    if (currentDayIndex !== null) {
      updateDayPlan(currentDayIndex, "verseObjId", selectedVerses);
    }

    setShowSearchModal(false);
    setCurrentDayIndex(null);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedVerses([]);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  };

  const selectVerse = (verse) => {
    const verseId = verse?._id || verse?.verseObjId || "";
    const verseText = verse?.verse_text || verse?.text || verse?.content || "";
    const verseReference = verse?.reference || `${verse?.book || ''} ${verse?.chapter || ''}:${verse?.verse || ''}`;
    const bookName = verse?.book_name || verse?.book || "";
    const chapter = verse?.chapter || "";
    const verseStart = verse?.verse || verse?.verse_start || "";

    const fullVerseObject = {
      id: `${bookName}-${chapter}-${verseStart}`,
      _id: verseId,
      text: verseText,
      reference: verseReference,
      book_name: bookName,
      book_id: verse?.book_id || "",
      chapter: chapter,
      verse_start: verseStart,
      filesetid: verse?.filesetid || "",
    };

    if (verseId && !selectedVerses.some(v => v._id === verseId)) {
      setSelectedVerses(prev => [...prev, fullVerseObject]);
      toast.success(`Verse added: ${verseReference}`);
    } else if (selectedVerses.some(v => v._id === verseId)) {
      toast.info("Verse already selected");
    }
  };

  const removeSelectedVerse = (verseId) => {
    setSelectedVerses(prev => prev.filter(v => v._id !== verseId));
    toast.info("Verse removed");
  };

  const clearSelectedVerses = () => {
    setSelectedVerses([]);
    toast.info("All verses cleared");
  };

  const addDayPlan = () => {
    if (dayPlans.length >= noofDays) {
      toast.error("Cannot add more days than specified");
      return;
    }

    setDayPlans([...dayPlans, {
      dayNumber: dayPlans.length + 1,
      dayPlanName: "",
      dayPlanDescription: "",
      youtubeVideoUrl: "",
      verseObjId: [],
      dayPlanDetailId: "",
    }]);
  };

  const updateDayPlan = (index, field, value) => {
    const updated = [...dayPlans];
    updated[index][field] = value;
    setDayPlans(updated);
  };

  const removeDayPlan = (index) => {
    if (dayPlans.length <= 1) {
      toast.error("At least one day plan is required");
      return;
    }

    const updated = [...dayPlans];
    updated.splice(index, 1);

    // Re-number days
    const renumbered = updated.map((day, idx) => ({
      ...day,
      dayNumber: idx + 1
    }));

    setDayPlans(renumbered);
    setnoofDays(renumbered.length);
  };

  const removeVerseFromDay = async (dayIndex, verseId, verse) => {
    const day = dayPlans[dayIndex];
    const planObjId = rowData?.planObjId || rowData?._id;
    const dayplanObjId = day?._id;
    const dayplandetObjId = verse?._id;

    const isExistingDetail = verseId && verseId.startsWith &&
      !verseId.startsWith('temp_') &&
      dayplandetObjId;

    if (isExistingDetail && planObjId && dayplanObjId && dayplandetObjId) {
      try {
        handleDeleteDayPlanDetail(
          dispatch,
          planObjId,
          dayplanObjId,
          dayplandetObjId,
          (res) => {
            if (res.type === 1) {
              const updated = [...dayPlans];
              if (updated[dayIndex]?.verseObjId) {
                updated[dayIndex].verseObjId = updated[dayIndex].verseObjId.filter(v => v._id !== verseId);
                setDayPlans(updated);
                toast.success("Verse deleted from server");
              }
            } else {
              toast.error(res.error || "Failed to delete verse from server");
            }
          }
        );
      } catch (error) {
                toast.error("Failed to delete verse");
      }
    } else {
      const updated = [...dayPlans];
      if (updated[dayIndex]?.verseObjId) {
        updated[dayIndex].verseObjId = updated[dayIndex].verseObjId.filter(v => v._id !== verseId);
        setDayPlans(updated);
        toast.info("Verse removed from day plan");
      }
    }
  };

  const onSaveOrUpdate = async () => {
    if (!planName.trim()) {
      toast.error("Enter plan name");
      return;
    }

    if (!selectedViewMode) {
      toast.error("Select a view mode");
      return;
    }

    // Validate day plans
    for (let i = 0; i < dayPlans.length; i++) {
      const day = dayPlans[i];
      if (!day.dayPlanName?.trim()) {
        toast.error(`Enter name for Day ${i + 1}`);
        return;
      }
    }

    let finalCategoryObjId = selectedCategory?._id;

    if (!finalCategoryObjId && searchTerm.trim()) {
      try {
        const categoryRes = await new Promise((resolve) => {
          dispatch(savePlanCategoryRequest({
            categoryName: searchTerm.trim(),
            layoutStyle: selectedViewMode,
            displayOrder: 1
          }, resolve));
        });

        if (categoryRes.type === 1) {
          const catData = Array.isArray(categoryRes.data) ? categoryRes.data[0] : categoryRes.data;
          finalCategoryObjId = catData?._id || catData?.categoryObjId;
          toast.success("New category saved!");
        }
      } catch (err) {
              }
    }

    const formattedDayPlans = dayPlans.map(day => ({
      _id: day._id,
      dayNumber: day.dayNumber,
      dayPlanName: day.dayPlanName || "",
      dayPlanDescription: day.dayPlanDescription || "",
      youtubeVideoUrl: day.youtubeVideoUrl || "",
      dayPlanDetailId: day.dayPlanDetailId || "",
      verseObjId: Array.isArray(day.verseObjId)
        ? day.verseObjId.map(verse => ({
            _id: verse._id,
            id: verse.id,
            text: verse.text,
            reference: verse.reference,
            book_name: verse.book_name,
            book_id: verse.book_id,
            chapter: verse.chapter,
            verse_start: verse.verse_start,
            filesetid: verse.filesetid,
          }))
        : []
    }));

    const planData = {
      planName: planName.trim(),
      description: description.trim(),
      noofDays: dayPlans.length,
      categoryObjId: finalCategoryObjId,
      layoutStyle: selectedViewMode
    };

    const isUpdate = !!rowData?.planObjId || !!rowData?._id;

    if (isUpdate) {
      handleUpdatePlanGlobal(
        dispatch,
        rowData,
        planData,
        galleryObjId,
        formattedDayPlans,
        (res) => {
          if (res.type === 1) {
            toast.success("Plan updated successfully!");
            onClose?.();
          } else {
            toast.error(res.message || "Update failed");
          }
        },
        typeofentity
      );
    } else {
      handleSavePlanGlobal(
        dispatch,
        planData,
        galleryObjId,
        formattedDayPlans,
        (res) => {
          if (res.type === 1) {
            toast.success("Plan saved successfully!");
            onClose?.();
          } else {
            toast.error(res.message || "Save failed");
          }
        },
        typeofentity
      );
    }
  };

  const SearchModal = () => {
    if (!showSearchModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1b1b1b] rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-white text-lg font-semibold">Search Bible Verses</h3>
              <button onClick={closeSearchModal} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            <p className="text-gray-400 text-sm mt-1">Select verses for Day {currentDayIndex !== null ? currentDayIndex + 1 : ""}</p>
          </div>

          {selectedVerses.length > 0 && (
            <div className="p-4 border-b border-gray-700">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-white text-sm font-medium">Selected Verses ({selectedVerses.length})</h4>
                <button onClick={clearSelectedVerses} className="text-red-400 hover:text-red-300 text-xs">Clear All</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedVerses.map((verse, index) => (
                  <div key={index} className="bg-[#2a2a2a] rounded-lg px-3 py-2 flex items-center gap-2">
                    <span className="text-white text-xs truncate max-w-[200px]">
                      {verse.book_name} {verse.chapter}:{verse.verse_start}
                    </span>
                    <button onClick={() => removeSelectedVerse(verse._id)} className="text-red-400 hover:text-red-300">
                      <AiOutlineDelete size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 border-b border-gray-700">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Type to search Bible verses..."
                className="w-full bg-[#2a2a2a] text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellowish"
                autoFocus
              />
              {isSearching && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-yellowish"></div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 overflow-y-auto max-h-[40vh]">
            {isSearching ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-yellowish mb-3"></div>
                <p className="text-gray-400">Searching...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-2">
                <div className="text-gray-400 text-sm mb-2">
                  Found {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                </div>
                {searchResults.map((verse, index) => {
                  const verseId = verse?._id || verse?.verseObjId || "";
                  const isSelected = selectedVerses.some(v => v._id === verseId);

                  return (
                    <div
                      key={index}
                      className={`bg-[#2a2a2a] hover:bg-[#333333] rounded-lg p-3 cursor-pointer transition-colors ${isSelected ? "ring-2 ring-yellowish" : ""}`}
                      onClick={() => selectVerse(verse)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-gray-400 font-medium">
                            {verse?.verse_text || verse?.text || ""}
                          </div>
                          <div className="text-white text-sm mt-1">
                            {verse?.book || ""} {verse?.chapter || ""}:{verse?.verse_start || ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isSelected ? (
                            <span className="text-green-400 text-sm font-medium">✓ Selected</span>
                          ) : (
                            <button className="text-yellowish hover:text-white text-sm font-medium flex items-center gap-1">
                              <AiOutlinePlus size={14} /> Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : searchQuery ? (
              <div className="text-center py-8">
                <p className="text-gray-400">No results found for "{searchQuery}"</p>
                <p className="text-gray-500 text-sm mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400">Type in the search box to find Bible verses</p>
                <p className="text-gray-500 text-sm mt-1">Search by book, chapter, verse, or keywords</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-700">
            <div className="flex justify-between items-center">
              <div className="text-gray-400 text-sm">
                {selectedVerses.length} verse{selectedVerses.length !== 1 ? "s" : ""} selected
              </div>
              <div className="flex gap-3">
                <button
                  onClick={closeSearchModal}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row h-full w-full flex-1 rounded-2xl overflow-hidden">
      <SearchModal />
      <div className="flex h-full flex-1">
        <LanguageDrawer
          languageData={languagesList}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setActiveLanguageDetails={setActiveLanguageDetails}
          languagemeta={languagemeta}
          setIsLanguageSave={setIsLanguageSave}
        />
        <div className="flex h-full flex-1">
          <div className="flex flex-col h-full w-full bg-greyishV2 bg-opacity-50 md:border-l md:border-r border-greyish border-opacity-30 overflow-auto">
            <div className="flex gap-4 items-center justify-between h-[64px] px-4 border-b border-[#28292a]">
              <p className="text-white font-semibold text-lg">
                {activeTab === "en" ? "Add Plan" : `Translate Plan to ${activeLanguageDetails?.language_name}`}
              </p>
              <div className="flex items-center gap-5">
                <button
                  className="text-yellowish bg-blackish font-medium text-[12px] border border-yellowish rounded-xl py-2 px-6"
                  onClick={onSaveOrUpdate}
                >
                  {rowData?.planObjId || rowData?._id ? "Update Plan" : "Save Plan"}
                </button>
              </div>
            </div>

            <div className="px-4 my-4 h-full overflow-y-auto">
              <div className="grid md:grid-cols-2 grid-cols-1 gap-x-4 gap-y-4 mx-1 w-full">
                <div className="md:col-span-1 relative">
                  <label className="text-[12px] text-greyish font-medium">Select Type</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setSelectedCategory(null);
                      setIsDropdownOpen(true);
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder="Search Categories..."
                    className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-10 border border-transparent focus:border-yellowish outline-none"
                  />
                  {isDropdownOpen && filteredOptions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-[#1b1b1b] border border-gray-700 rounded-md shadow-2xl max-h-48 overflow-y-auto">
                      {filteredOptions.map((category, index) => (
                        <div
                          key={index}
                          className="p-3 hover:bg-[#333] cursor-pointer text-white text-[13px] border-b border-gray-800 last:border-0"
                          onClick={() => {
                            setSearchTerm(category.categoryName);
                            setSelectedCategory(category);
                            if (category.layoutStyle) {
                              setSelectedViewMode(Number(category.layoutStyle));
                            }
                            setIsDropdownOpen(false);
                          }}
                        >
                          {category.categoryName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-1">
                  <label className="text-[12px] text-greyish font-medium block mb-2">View Modes</label>
                  <div className="flex flex-row flex-wrap gap-4 mt-3">
                    {[
                      { label: "Linear", value: 1 },
                      { label: "Grouped", value: 2 },
                      { label: "List", value: 3 },
                    ].map((view) => (
                      <label key={view.value} className="flex items-center gap-2 cursor-pointer group">
                        <div className="relative flex items-center justify-center">
                          <input
                            type="radio"
                            name="viewMode"
                            checked={selectedViewMode === view.value}
                            onChange={() => setSelectedViewMode(view.value)}
                            className="sr-only"
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 transition-all flex items-center justify-center ${
                              selectedViewMode === view.value
                                ? "border-yellowish bg-yellowish"
                                : "border-gray-500 bg-transparent group-hover:border-greyish"
                            }`}
                          >
                            {selectedViewMode === view.value && (
                              <div className="w-2 h-2 bg-black rounded-full shadow-sm"></div>
                            )}
                          </div>
                        </div>
                        <span className="text-[13px] text-white font-medium">{view.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-[12px] text-greyish font-medium">Plan Name *</label>
                  <input
                    type="text"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder="Enter Plan Name"
                    className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-10"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-[12px] text-greyish font-medium">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-24"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="text-[12px] text-greyish font-medium">No. of Days *</label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={noofDays}
                    onChange={(e) => {
                      const value = Math.max(1, Math.min(30, Number(e.target.value) || 1));
                      setnoofDays(value);
                    }}
                    className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-10"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-[12px] text-greyish font-medium mb-2 block">Cover Image</label>
                  <UploadControlsV2
                    title="Cover Image"
                    subtitle="Upload Cover Image"
                    tooltipTitle="Upload cover image"
                    showMobileUpload={false}
                    showTabletUpload={false}
                    showWebUpload={true}
                    webUploadProgressId="planCoverImageUpload"
                    webUploadName="webUploadUrl"
                    webUploadUrl={coverImageUrls.webUploadUrl}
                    handleWebChangeUrl={handleCoverImageInputChange}
                    handleWebFileUpload={(e) =>
                      handleCoverImageFileInputChange(
                        setCoverImageUrls,
                        "webUploadUrl",
                        e.target.files?.[0]
                      )
                    }
                  />
                </div>

                <div className="md:col-span-2 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-semibold">Day Plans</h3>
                    <button
                      onClick={addDayPlan}
                      className="bg-yellowish text-black px-3 py-1 rounded-md text-[12px]"
                    >
                      Add Day Plan
                    </button>
                  </div>

                  {dayPlans.map((day, index) => {
                    const verses = Array.isArray(day.verseObjId) ? day.verseObjId : [];

                    return (
                      <div key={index} className="border border-greyish border-opacity-30 p-4 mb-4 rounded-xl bg-blackish bg-opacity-20">
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-yellowish font-bold">Day {day.dayNumber}</span>
                          {dayPlans.length > 1 && (
                            <button
                              onClick={() => removeDayPlan(index)}
                              className="text-red-500 text-[12px] hover:text-red-400 bg-black bg-opacity-30 px-2 py-1 rounded"
                            >
                              Remove Day
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="text-[11px] text-greyish font-medium">Day Plan Name *</label>
                            <input
                              type="text"
                              placeholder="Enter day plan name"
                              value={day.dayPlanName}
                              onChange={(e) => updateDayPlan(index, "dayPlanName", e.target.value)}
                              className="bg-blackish p-2 w-full text-white text-[13px] mt-1 rounded-md h-10"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="text-[11px] text-greyish font-medium">Description</label>
                            <textarea
                              placeholder="Enter day plan description"
                              value={day.dayPlanDescription}
                              onChange={(e) => updateDayPlan(index, "dayPlanDescription", e.target.value)}
                              className="bg-blackish p-2 w-full text-white text-[13px] mt-1 rounded-md h-20"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="text-[11px] text-greyish font-medium">YouTube Video URL</label>
                            <input
                              type="text"
                              placeholder="https://youtube.com/..."
                              value={day.youtubeVideoUrl}
                              onChange={(e) => updateDayPlan(index, "youtubeVideoUrl", e.target.value)}
                              className="bg-blackish p-2 w-full text-white text-[13px] mt-1 rounded-md h-10"
                            />
                          </div>
                        </div>

                        <div className="mt-4 border-t border-greyish border-opacity-20 pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-[11px] text-greyish font-medium uppercase tracking-wider">Selected Verses</label>
                            <button
                              onClick={() => openSearchModal(index)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors shadow-sm"
                            >
                              <AiOutlinePlus size={14} /> {verses.length > 0 ? "Add More" : "Add Verses"}
                            </button>
                          </div>
                          {verses.length > 0 ? (
                            <div className="grid grid-cols-1 gap-2">
                              {verses.map((verse, verseIndex) => (
                                <div key={verseIndex} className="bg-black bg-opacity-40 border border-greyish border-opacity-10 rounded-xl p-3 flex justify-between items-center group hover:border-opacity-30 transition-all">
                                  <div className="flex-1">
                                    <div className="text-white text-[13px] mb-1 font-medium">{verse.text}</div>
                                    <div className="text-greyish text-[11px] flex items-center gap-2">
                                      <span className="bg-yellowish bg-opacity-10 text-yellowish px-1.5 py-0.5 rounded text-[10px] font-bold">
                                        {verse.book_name} {verse.chapter}:{verse.verse_start}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => removeVerseFromDay(index, verse._id, verse)}
                                    className="text-red-400 hover:text-red-300 ml-2 p-1.5 hover:bg-red-500 hover:bg-opacity-10 rounded-full transition-all"
                                    title="Remove verse"
                                  >
                                    <AiOutlineDelete size={18} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-greyish text-sm italic text-center py-6 bg-black bg-opacity-10 rounded-xl border border-dashed border-greyish border-opacity-20">
                              No verses selected. Click "Add Verses" to begin.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CollapsibleRightPanel title="Stats / Layouts">
        <CustomTabsV2
          tabHeight={"h-[64px]"}
          tabsTitleArray={["Stats", "Layouts"]}
          tabsContentObject={{
            Stats: (
              <div className="mt-8 flex items-center justify-center w-full text-white">
                {rowData?.planObjId || rowData?._id ? (
                  <div className="w-full px-8">
                    <p>Stats content for plan...</p>
                  </div>
                ) : (
                  <>No stats available for plan</>
                )}
              </div>
            ),
            Layouts: (
              <div className="mt-8 flex items-center justify-center w-full text-white">
                Layouts management for plans...
              </div>
            ),
          }}
        />
      </CollapsibleRightPanel>

      {coverSelectedImage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] px-4">
          <button
            className="absolute top-6 right-6 bg-black/60 p-3 rounded-full hover:bg-black"
            onClick={closeCoverCropper}
            disabled={coverIsCropping}
          >
            <AiOutlineClose className="text-white w-6 h-6" />
          </button>

          {coverSelectedImage.previewOnly ? (
            <div className="bg-gray-900 p-6 rounded-lg max-w-[1100px] w-full">
              <img
                src={coverSelectedImage.url}
                alt="preview"
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            </div>
          ) : (
            <div className="bg-gray-900 p-6 rounded-lg max-w-[1100px] w-full">
              <div className="flex justify-center mb-4 gap-3">
                {["desktop", "tablet", "mobile"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setCoverActiveTab(tab);
                      setCoverCrop({ x: 0, y: 0 });
                      setCoverZoom(1);
                      setCoverCroppedAreaPixels(null);
                    }}
                    className={`px-4 py-2 rounded-md text-sm ${
                      coverActiveTab === tab
                        ? "bg-yellowish text-black font-semibold"
                        : "bg-gray-700 text-white"
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full max-w-[90vw] h-[60vh] md:h-[70vh] relative bg-black rounded-md overflow-hidden">
                    <Cropper
                      image={coverSelectedImage.url}
                      crop={coverCrop}
                      zoom={coverZoom}
                      aspect={getCoverAspect(coverActiveTab)}
                      onCropChange={setCoverCrop}
                      onCropComplete={onCoverCropComplete}
                      onZoomChange={setCoverZoom}
                      objectFit="contain"
                      minZoom={0.5}
                      maxZoom={3}
                    />
                  </div>

                  <div className="w-full max-w-[600px] mt-4">
                    <label className="block text-sm mb-1">
                      Zoom: {coverZoom.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min={0.5}
                      max={3}
                      step={0.01}
                      value={coverZoom}
                      onChange={(e) => setCoverZoom(Number(e.target.value))}
                      className="w-full"
                      disabled={coverIsCropping}
                    />
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleCoverCropSave}
                      disabled={!coverCroppedAreaPixels || coverIsCropping}
                      className="bg-yellowish text-black px-4 py-2 rounded-md font-semibold hover:bg-yellow-600 disabled:opacity-50"
                    >
                      {coverIsCropping
                        ? "Cropping..."
                        : `Save ${coverActiveTab} Crop`}
                    </button>
                  </div>
                </div>

                <div className="w-full md:w-[320px] flex flex-col gap-4">
                  <div className="text-sm text-gray-200 mb-2">Previews</div>
                  <div className="flex flex-col gap-3">
                    {["desktop", "tablet", "mobile"].map((tab) => (
                      <div
                        key={tab}
                        className="bg-black/40 p-2 rounded-md flex items-center gap-3"
                      >
                        <div className="w-20 h-12 bg-gray-800 rounded overflow-hidden flex items-center justify-center">
                          <img
                            src={
                              coverCroppedImages[tab] ||
                              (tab === coverActiveTab
                                ? coverSelectedImage.url
                                : "/placeholder.jpg")
                            }
                            alt={`${tab}-preview`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 text-xs text-gray-100">
                          <div className="font-medium">
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          </div>
                          <div className="text-[11px] text-gray-300">
                            {coverCroppedImages[tab] ? "✓ Uploaded" : "Not set"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  function handleCoverImageInputChange(e) {
    const { name, value } = e.target;
    setCoverImageUrls((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCoverImage(value);

    if (!value) return;

    const allowedExtensions = [".jpg", ".jpeg", ".png"];
    const urlLower = value.toLowerCase();
    const isValid = allowedExtensions.some((ext) => urlLower.endsWith(ext));
    if (!isValid) {
      toast.error("Only JPG and PNG image URLs are allowed!");
      return;
    }

    loadCoverImageAsBlob(value)
      .then(({ blob, blobUrl }) => {
        coverOriginalBlobRef.current = { blob, url: blobUrl };
        setCoverSelectedImage({ url: blobUrl, previewOnly: false });
        setCoverCroppedImages({ desktop: value, tablet: value, mobile: value });
        setCoverActiveTab("desktop");
        setCoverCrop({ x: 0, y: 0 });
        setCoverZoom(1);
        setCoverCroppedAreaPixels(null);
      })
      .catch(() => {
        setCoverSelectedImage({ url: value, previewOnly: true });
        setCoverCroppedImages({ desktop: value, tablet: value, mobile: value });
        toast.warning(
          "Image loaded (view only). For cropping, please upload the file directly."
        );
      });
  }

  function handleCoverImageFileInputChange(setState, keyName, file) {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG images are allowed!");
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    coverOriginalBlobRef.current = { blob: file, url: blobUrl };
    setCoverSelectedImage({ url: blobUrl, previewOnly: false, file });
    setCoverCroppedImages({ desktop: "", tablet: "", mobile: "" });
    setCoverActiveTab("desktop");
    setCoverCrop({ x: 0, y: 0 });
    setCoverZoom(1);
    setCoverCroppedAreaPixels(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("downloadable", false);

    dispatch(
      uploadAssestsV2Action({
        apiPayloadRequest: formData,
        uploadProgressId: "planCoverImageUpload",
        callback: (res) => {
          if (res?.url) {
            setState((prev) => ({
              ...prev,
              [keyName]: res.url,
            }));
            setCoverImage(res.url);
            setCoverCroppedImages({
              desktop: res.url,
              tablet: res.url,
              mobile: res.url,
            });
          }
        },
      })
    );
  }
};

export default GlobalPlanForm;
